/**
 * Registers the production service worker only in safe contexts.
 *
 * Following the Lovable PWA skill:
 *  - NEVER register in dev, the Lovable preview iframe, or any *.lovableproject.com / preview host.
 *  - Honour ?sw=off as a kill-switch.
 *  - In any refused context, proactively unregister an existing /sw.js so a
 *    stale worker from a previous deploy can't keep serving cached HTML.
 */
export async function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  const url = new URL(window.location.href);
  const host = window.location.hostname;
  const isPreviewHost =
    host.startsWith("id-preview--") ||
    host.startsWith("preview--") ||
    host === "lovableproject.com" ||
    host.endsWith(".lovableproject.com") ||
    host === "lovableproject-dev.com" ||
    host.endsWith(".lovableproject-dev.com") ||
    host === "beta.lovable.dev" ||
    host.endsWith(".beta.lovable.dev");

  const inIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  const killSwitch = url.searchParams.get("sw") === "off";
  const refuse = !import.meta.env.PROD || inIframe || isPreviewHost || killSwitch;

  if (refuse) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        regs
          .filter((r) => {
            const scriptUrl = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || "";
            return scriptUrl.endsWith("/sw.js");
          })
          .map((r) => r.unregister()),
      );
    } catch {
      /* noop */
    }
    return;
  }

  try {
    const { Workbox } = await import("workbox-window");
    const wb = new Workbox("/sw.js");
    // When a new SW is waiting, activate it so the next navigation gets the
    // fresh shell. We deliberately do NOT call window.location.reload() on
    // controllerchange — auto-reloading mid-flow (e.g. right after sign-in)
    // produced redirect loops and "stuck on white screen" reports. The
    // NetworkFirst HTML strategy already ensures users see fresh markup on
    // their next normal navigation.
    wb.addEventListener("waiting", () => {
      wb.messageSkipWaiting();
    });
    await wb.register();
  } catch {
    /* swallow — SW failures must never break the app */
  }
}

